// Стадия проекта — ЕДИНСТВЕННЫЙ источник правды о том, где мы по жизненному циклу
// игры: development → pre-release → operations (docs/project-stages.md). От неё
// зависят инженерные политики; первая из них — кэширование медиа-ассетов.
//
// Почему это вообще есть: на разработке арт перезаливается в S3 поверх тех же
// ключей, и браузер показывает старый файл (RGW отдавал объекты без Cache-Control
// → эвристический кэш по Last-Modified, руками его не сбросить). Два рычага
// работают в паре: заливка ставит на объекты `no-cache, must-revalidate`
// (tools/media/*.sh — ревалидация по ETag, дёшево), а этот модуль выдаёт токен
// антикэша для URL — он пробивает даже те записи кэша, что легли ДО починки
// заголовков, и лечит отравленные no-CORS ответы (corsImage.ts).
//
// Токен один на вкладку (sessionStorage): в пределах сессии URL стабильны, медиа
// не перекачивается на каждый F5 (свежесть внутри сессии даёт ревалидация по
// ETag), а новая вкладка/`resetAssetCacheBust()` из дев-панели даёт чистый лист.

export type ProjectStage = 'development' | 'pre-release' | 'operations';

const STAGES: readonly string[] = ['development', 'pre-release', 'operations'];

/** Ключ токена антикэша в sessionStorage (экспортирован для тестов/дев-панели). */
export const ASSET_BUST_KEY = 'dg_asset_bust_v1';

/** Чистая резолюция стадии: явный VITE_STAGE побеждает, иначе — по режиму сборки
 *  (dev-сервер = разработка, прод-бандл = операционка). Мусорное значение
 *  игнорируем: опечатка в .env не должна молча включать прод-политику. */
export function resolveStage(env: { VITE_STAGE?: string; DEV?: boolean }): ProjectStage {
  const explicit = env.VITE_STAGE;
  if (explicit && STAGES.includes(explicit)) return explicit as ProjectStage;
  return env.DEV ? 'development' : 'operations';
}

export const PROJECT_STAGE: ProjectStage = resolveStage(import.meta.env);

// Счётчик отличает токены, выданные в одну миллисекунду (reset подряд).
let seq = 0;

function newToken(): string {
  return `${Date.now().toString(36)}-${++seq}`;
}

/** Токен антикэша для медиа-URL; undefined вне стадии development (там кэш нужен).
 *  Хранится в sessionStorage: переживает F5, но не переживает новую вкладку. */
export function assetCacheBust(stage: ProjectStage = PROJECT_STAGE): string | undefined {
  if (stage !== 'development') return undefined;
  try {
    const saved = sessionStorage.getItem(ASSET_BUST_KEY);
    if (saved) return saved;
    const token = newToken();
    sessionStorage.setItem(ASSET_BUST_KEY, token);
    return token;
  } catch {
    return newToken();  // приватный режим без стораджа — живём одним рендером
  }
}

/** URL с токеном стадии: `?v=<token>` (или `&v=`, если query уже есть). Вне
 *  development — URL как был. Клеится на ГРАНИЦЕ ЗАГРУЗКИ (corsImage.ts), а не в
 *  строителях URL: так ни один новый строитель нельзя забыть пометить. Плеер —
 *  блекбокс со своей копией той же логики (assets.ts, токен от media.cacheBust);
 *  токен один и тот же, значит и URL у конструктора с плеером совпадают. */
export function withAssetCacheBust(url: string, stage: ProjectStage = PROJECT_STAGE): string {
  const token = assetCacheBust(stage);
  if (!token) return url;
  return `${url}${url.includes('?') ? '&' : '?'}v=${encodeURIComponent(token)}`;
}

/** Выбросить текущий токен: следующий запрос медиа пойдёт по новым URL. */
export function resetAssetCacheBust(): void {
  try { sessionStorage.removeItem(ASSET_BUST_KEY); } catch { /* in-memory only */ }
}
